/**
 * Project switcher modal ("My Projects").
 *
 * Phase 6 UI (Tier 2). Shares a layout-and-styling skeleton with the auth
 * modal, and calls the auth-api.js client which sends the `pb_token` cookie via
 * credentials: 'include'.
 *
 *  - showProjectSwitcherModal(): list of the user's projects + open + delete
 *
 * HR-7 (2026-08-27) removed this file's second export, `showProjectHistoryModal`
 * — the Version History modal. Its replacement is the docked timeline, which
 * previews the selected version on the real canvas instead of describing it in
 * a list. The filename is deliberately UNCHANGED: it is named in the sweep
 * allowlist, the suppressions ledger and half a dozen suites, and every one of
 * those ledgers shrinks only, so renaming the file would cost more edits to
 * shrink-only files than the tidier name is worth.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { isLoadSupersededError, restoreCloudVersion, cloudSave } from '../core/save-load.js';
import { reportError } from '../core/report-error.js';
import { postProjectDeleted } from '../core/lifecycle-channel.js';
import { getProfile, saveProfile } from '../core/profile-store.js';
import { isShare, watermarkFor, shouldAdvanceWatermark } from '../core/unseen-shares.js';
import { adoptProjectList, recomputeUnseenShares } from '../core/share-inbox.js';

/**
 * Record that the shares in `projects` have been seen (SB-5). Best-effort and
 * never awaited by the render path: a failed watermark write means the badge
 * shows again next time, which is a far better failure than a lost notification.
 */
async function markSharesSeen(projects) {
  if (!shouldAdvanceWatermark(projects, getProfile()?.sharesSeenAt)) return;
  try {
    await saveProfile({ sharesSeenAt: watermarkFor(projects) });
    recomputeUnseenShares();
  } catch (err) {
    reportError('Share watermark', err, {
      user: 'silent',
      detail: 'could not record which shared projects have been seen',
      id: 'shares-seen-watermark'
    });
  }
}
import { deleteProject, listProjects, removeProjectMember } from '../templates/auth-api.js';
import { TemplateApiError } from '../templates/template-api.js';
import { loadThumbnailObjectUrl, releaseAllThumbnails } from '../preview/project-thumbnail.js';
import { showToast, showConfirmModal, getCurrentUser } from './shell.js';
import { createModal, createButton } from './components.js';
import { openCollectionBrowser, rememberedCount, rememberCount } from './collection-browser.js';
import { summarizeSelection } from './section-list-interaction.js';
import { icon } from './icons.js';

/**
 * How many skeleton cards the switcher paints while the list is in flight.
 *
 * A fixed 6 meant a two-row placeholder resolving into however many rows the
 * account actually has, so the modal jumped height on every open — for a
 * twelve-project library it doubled. The count can't be known before the
 * fetch, so the LAST known one is remembered per device and used as the
 * estimate; it is exact on every open after the first, and the bounds keep a
 * corrupted or absurd value from painting a one-card or thousand-card grid.
 *
 * CB-9: the clamp and the storage are now the module's `rememberedCount`
 * (which is this function, generalized in CB-5a) — same key, same 4..24
 * bounds, same fallback of 6.
 */
const PROJECT_COUNT_KEY = 'pb:projects-last-count';
const SKELETON_MIN = 4;
const SKELETON_MAX = 24;

function skeletonCount() {
  return rememberedCount(PROJECT_COUNT_KEY, { min: SKELETON_MIN, max: SKELETON_MAX, fallback: 6 });
}

function rememberProjectCount(count) {
  rememberCount(PROJECT_COUNT_KEY, count);
}

function formatDate(ms) {
  if (!Number.isFinite(ms)) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

/** Compact "when" for switcher cards; the exact stamp rides the title attr. */
function relativeDate(ms) {
  if (!Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return `${days}d ago`;
  }
}

/**
 * Up to two initials for the fallback tile.
 *
 * (HR-7: `dayKeyOf` / `dayLabelOf` left with the version list. They were LIFTED
 * into `ui/history-rail/version-model.js` in HR-1, not re-derived, which is why
 * deleting them here changes no day header anywhere.)
 */
function initialsOf(name) {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * Stable hue per project so a card keeps the same tile colour across sessions
 * (and so two projects named alike don't collide). FNV-1a over the id.
 */
function hueOf(id) {
  let hash = 2166136261;
  const text = String(id || '');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 360;
}

/**
 * Glyphs come from the registry. Sizing is a class in every case — the
 * hand-drawn originals carried `width="15" height="15"`, which no media query
 * can reach. (`search` and `close` retired with the hand-rolled shell in CB-9:
 * the module's toolbar and its bulk bar own those two affordances now.)
 */
const CARD_ICONS = {
  history: icon('history', { size: 'sm' }),
  trash: icon('trash', { size: 'sm' }),
  leave: icon('logout', { size: 'sm' })
};

/**
 * Three-way confirm shown when opening a different project with unsaved local
 * changes. Resolves 'save-first' | 'open-anyway' | 'cancel'. The switcher is only
 * reachable while signed in, so a cloud save is always available.
 * @param {string} targetName
 * @returns {Promise<'save-first'|'open-anyway'|'cancel'>}
 */
function askOpenDirtyChoice(targetName, { canSaveCurrent = true } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    const { body, footer, close } = createModal({
      title: 'Open a different project?',
      overlayClassName: 'pb-confirm-overlay',
      withFooter: true,
      onClose: () => done('cancel'),
      maxWidth: 480
    });
    const msg = document.createElement('p');
    msg.className = 'pb-confirm-msg';
    msg.textContent = `You have unsaved changes. Opening “${targetName}” will replace your current project.`;
    body.appendChild(msg);
    footer.classList.add('pb-confirm-footer');
    const actions = [
      createButton({
        variant: 'ghost',
        label: 'Cancel',
        onClick: () => {
          done('cancel');
          close();
        }
      }),
      createButton({
        variant: 'ghost',
        label: 'Open anyway',
        onClick: () => {
          done('open-anyway');
          close();
        }
      })
    ];
    if (canSaveCurrent) {
      actions.push(
        createButton({
          variant: 'primary',
          label: 'Cloud-save & open',
          onClick: () => {
            done('save-first');
            close();
          }
        })
      );
    }
    footer.append(...actions);
  });
}

// ---------- Project switcher ----------

/**
 * My Projects (CB-9) — the initiative's second donor becomes a consumer.
 *
 * The module now owns the frame it originally donated: the poster grid and its
 * in-place filtering, the search field, the item registry, the skeletons, the
 * empty/error states, the thumbnail pool, the selection store and the floating
 * bulk bar. Multi-select is no longer a local `Set` — which is how this surface
 * GAINS Shift-range and Cmd-click, roving grid key nav, Escape-clears-selection
 * and run-id/abort guarding, none of which it had.
 *
 * WHAT DELIBERATELY DID NOT MOVE, and the reason it did not:
 * `dropProjects` / `restoreProjects` / `confirmAndDeleteProjects` / the by-id
 * correlation map / `deleteInFlight` stay here, verbatim. A shared collection
 * surface never mutates: the module supplies selection state and bulk-bar
 * chrome and awaits an intent; THIS file performs the delete and owns the
 * rollback. Anything else re-derives the ascending-index refill and the by-id
 * correlation from scratch — and until this phase neither had a test.
 */
export async function showProjectSwitcherModal({ canOpenProject, beforeOpenProject } = {}) {
  const currentProjectId = store.getState()?.projectId || '';

  /** @type {ReturnType<typeof openCollectionBrowser>} */
  let api = null;
  let projects = [];
  let loadErrorMessage = null;
  // SB-5. Captured ONCE at open, before any watermark advance, so the chips
  // stay stable while the modal is on screen. Reading it live would make every
  // "New" chip vanish the moment the PATCH lands, which is the opposite of
  // telling someone what changed.
  const seenWatermark = Number.isFinite(Number(getProfile()?.sharesSeenAt))
    ? Number(getProfile().sharesSeenAt)
    : null;

  /**
   * How many selected projects the current search is hiding.
   *
   * The selection deliberately SURVIVES a search — narrowing it to the visible
   * rows would be its own data-loss surprise in the other direction (select 5,
   * filter, delete, and four of them quietly live). What must not survive is
   * the selection being invisible: the count is what the user reads before
   * pressing Delete. Resolved at press time, never captured.
   */
  function hiddenSelectionCount() {
    if (!api) return 0;
    return summarizeSelection(api.getSelectedIds(), new Set(api.getVisibleIds())).hiddenCount;
  }

  /**
   * The poster's contents. The module owns the BOX (`.pb-coll-poster` carries
   * the aspect-ratio, the radius and the elevation — the layout-stability
   * doctrine that the box reserves the space, not the image); this owns what
   * sits inside it. DOM-first: every project string lands via textContent.
   */
  function buildPoster(project) {
    const id = String(project.id || '');
    const name = project.name || 'Untitled';
    const frag = document.createDocumentFragment();

    // The monogram tile is always underneath, so a project with no capture yet
    // is a coloured tile rather than a hole in the grid.
    const tile = document.createElement('div');
    tile.className = 'pb-project-card-tile';
    tile.setAttribute('aria-hidden', 'true');
    tile.style.setProperty('--pb-card-hue', String(hueOf(id)));
    tile.textContent = initialsOf(name);
    frag.appendChild(tile);

    // The <img> exists ONLY for a project that has a capture — no element, no
    // pool entry, no wasted request through the access-checked thumb route.
    if (Number.isFinite(Number(project.thumbUpdatedAt))) {
      const img = document.createElement('img');
      img.className = 'pb-project-card-img';
      img.alt = '';
      img.decoding = 'async';
      img.dataset.cbThumb = '';
      img.dataset.thumbId = id;
      img.dataset.thumbAt = String(project.thumbUpdatedAt);
      img.hidden = true;
      frag.appendChild(img);
    }

    const chips = document.createElement('div');
    chips.className = 'pb-project-card-chips';
    if (project.id === currentProjectId) {
      const badge = document.createElement('span');
      badge.className = 'pb-project-card-badge';
      badge.textContent = 'Editing now';
      chips.appendChild(badge);
    }
    if (project.role === 'member') {
      // SB-5: NEW is distinct from Shared. "Shared" is a permanent role label —
      // identical on day 1 and day 100 — which is why it never told a recipient
      // anything. This one is transient and answers "since I last looked".
      if (isShare(project) && Number(project.memberSince) > (seenWatermark ?? -1)) {
        const isNew = document.createElement('span');
        isNew.className = 'pb-share-role-chip is-new pb-project-card-role';
        isNew.textContent = 'New';
        chips.appendChild(isNew);
      }
      const role = document.createElement('span');
      role.className = 'pb-share-role-chip is-member pb-project-card-role';
      role.textContent = 'Shared';
      chips.appendChild(role);
    }
    frag.appendChild(chips);

    const version = document.createElement('span');
    version.className = 'pb-project-card-version';
    version.setAttribute('aria-hidden', 'true');
    version.textContent = `v${project.latestVersion}`;
    frag.appendChild(version);

    return frag;
  }

  // NOT migrated to the transactional-undo / runWithUndo defer pattern (toast adoption
  // Phase 5): this soft-deletes via the remote API but there is no undelete endpoint
  // (scripts/ai/project-handlers.js), and pagehide/visibilitychange cannot guarantee an
  // async remote fetch lands. A future defer-then-commit here is gated on a server
  // undelete endpoint (or keepalive/sendBeacon). Keeps its confirm modal for now.
  let deleteInFlight = false;

  async function deleteProjects(items, { hiddenCount = 0 } = {}) {
    // Defensive: a member-role row can never be deleted from this account —
    // the server would 403 owner_only; the UI offers Leave instead. SB-2 adds
    // the open project for the same reason the guard sits here rather than on
    // either button: this function is reachable from a row action AND the bulk
    // bar, so a single chokepoint is the only place the rule cannot be missed.
    const targets = items.filter(
      (item) => item?.id && item.role !== 'member' && String(item.id) !== currentProjectId
    );
    if (!targets.length) return;
    // Reachable from a row button and from the bulk bar, so the guard has
    // to sit on the function rather than on either button.
    if (deleteInFlight) return;
    deleteInFlight = true;
    try {
      await confirmAndDeleteProjects(targets, hiddenCount);
    } finally {
      deleteInFlight = false;
    }
  }

  /**
   * Drop rows from the model and pull their cards out of the live grid, without
   * re-rendering. A rebuild would throw away the decoded thumbnails and
   * re-fetch all of them through the access-checked route — a whole library's
   * worth of requests to reflect one deletion. `api.removeRecords` is that same
   * contract, module-side: it detaches the elements and prunes the selection
   * and never re-renders a survivor.
   *
   * Returns the removed projects paired with the index they held, so a failed
   * delete can put them back exactly where they were.
   */
  function dropProjects(ids) {
    const wanted = new Set(ids.map(String));
    const removed = [];
    for (let i = projects.length - 1; i >= 0; i -= 1) {
      const id = String(projects[i].id || '');
      if (!wanted.has(id)) continue;
      removed.unshift({ index: i, project: projects[i] });
      projects.splice(i, 1);
    }
    api?.removeRecords([...wanted]);
    return removed;
  }

  function restoreProjects(removed) {
    // Refill in ascending index order — each stored index is only the right
    // slot once every lower one has been put back.
    for (const { index, project } of [...removed].sort((a, b) => a.index - b.index)) {
      projects.splice(Math.min(index, projects.length), 0, project);
    }
    api?.setRecords(projects);
  }

  /**
   * Reflect whatever `projects` now says. The grid was mutated in place and the
   * module repaints its own empty state off the surviving entry count, so the
   * only thing left here is the remembered skeleton count.
   */
  function afterProjectsShrank() {
    rememberProjectCount(projects.length);
  }

  // Kept OUT of the onConfirm seam deliberately. This is a batch with
  // partial-failure semantics — Promise.allSettled means "3 of 5 deleted" is a
  // real outcome — and onConfirm only models all-or-nothing: throwing would
  // hold the dialog open and hide the deletions that DID land. The dialog
  // therefore stays a plain confirm.
  //
  // The delete is OPTIMISTIC. It used to replace the entire body with one line
  // of centred text, collapsing the modal to a couple of hundred pixels, and
  // then reload the whole list from the server, so removing one card of twelve
  // cost a full-height flicker and a re-fetch of every thumbnail. The cards now
  // leave the grid the moment the confirm is accepted, and only a rejected
  // delete puts one back.
  async function confirmAndDeleteProjects(targets, hiddenCount = 0) {
    const label =
      targets.length === 1 ? `“${targets[0].name || 'Untitled'}”` : `${targets.length} projects`;
    // The second disclosure, and the one that catches the dangerous case: the
    // user filtered down to a single visible card, forgot the earlier
    // selection, and is about to delete rows they cannot see. The bulk bar
    // already says so; the confirm says it again at the moment of commitment,
    // because that is the last point where the answer is still "no".
    const hiddenNote =
      hiddenCount > 0
        ? `${hiddenCount} of ${hiddenCount === 1 ? 'them is' : 'them are'} hidden by your current search. `
        : '';
    const confirmed = await showConfirmModal({
      title: targets.length === 1 ? 'Delete project' : 'Delete projects',
      message: `Delete ${label}? ${targets.length === 1 ? 'It' : 'They'} can’t be restored from here.`,
      details: `${hiddenNote}If it’s published, its site goes offline too. Files you’ve downloaded to your computer are not affected.`,
      confirmLabel: targets.length === 1 ? 'Delete' : 'Delete Selected',
      danger: true
    });
    if (!confirmed) return;

    const removed = dropProjects(targets.map((item) => item.id));
    afterProjectsShrank();

    const results = await Promise.allSettled(targets.map((item) => deleteProject(item.id)));
    // Correlated by id, not by position: dropProjects() returns entries in grid
    // order while `results` follows `targets`, and the two only coincide when
    // the selection happens to have been made top-to-bottom.
    const byId = new Map(removed.map((entry) => [String(entry.project.id || ''), entry]));
    // SB-2 (A6): tell this account's other tabs, per project, and ONLY for the
    // ones the server actually deleted. The optimistic row removal above is
    // rolled back on rejection, so posting before this point would blank a
    // healthy tab on a delete that then failed. The poster knows the cause, so
    // the message carries `signal` and the receiver never re-derives it from an
    // ambiguous 404.
    targets.forEach((item, i) => {
      if (results[i]?.status !== 'fulfilled') return;
      postProjectDeleted(String(item.id));
    });
    const rolledBack = targets
      .filter((item, i) => results[i]?.status === 'rejected')
      .map((item) => byId.get(String(item.id)))
      .filter(Boolean);
    if (rolledBack.length > 0) {
      restoreProjects(rolledBack);
      rememberProjectCount(projects.length);
      showToast(
        `${rolledBack.length} project${rolledBack.length === 1 ? '' : 's'} could not be deleted`,
        'error'
      );
    } else {
      showToast(`Deleted ${label}`, 'success');
    }
  }

  async function leaveProject(project) {
    // Collab Phase 2: a member removes THEMSELVES from a shared project.
    const id = String(project?.id || '');
    const name = project?.name || 'this project';
    const selfSub = getCurrentUser()?.sub ? String(getCurrentUser().sub) : '';
    if (!id || !selfSub) return;
    const left = await showConfirmModal({
      title: 'Leave project',
      message: `Leave "${name}"? You'll lose access until the owner adds you again.`,
      confirmLabel: 'Leave',
      confirmBusyLabel: 'Leaving…',
      danger: true,
      errorScope: 'Project leave',
      onConfirm: async () => {
        try {
          await removeProjectMember(id, selfSub);
        } catch (err) {
          throw new Error(
            err instanceof TemplateApiError ? err.message : 'Could not leave the project',
            { cause: err }
          );
        }
      }
    });
    if (!left) return;
    showToast(`Left ${name}`, 'success');
    // Same treatment as delete: the row is gone, so take the row out. The
    // confirm dialog already held the failure — reaching here means the
    // server accepted it, and there is nothing a reload would tell us.
    dropProjects([id]);
    afterProjectsShrank();
  }

  /**
   * @returns {Promise<boolean>} whether `project` is the live project when this
   *   resolves — true after a successful open AND for the already-open card.
   *   HR-7 needs the answer: the card's history action opens the project and
   *   then asks for its timeline, and a timeline must never be offered for a
   *   project the open did not actually reach.
   */
  async function openProject(project) {
    const id = String(project.id || '');
    const latestVersion = Number(project.latestVersion);
    if (!id || !Number.isInteger(latestVersion)) return false;
    if (id === currentProjectId) {
      api?.close();
      return true;
    } // already open
    if (typeof canOpenProject === 'function' && !canOpenProject(project)) return false;
    // A caller without the destination-lock hook cannot safely replace state
    // from a read-only tab. The shell supplies the hook for project switching.
    if (!store.isWritable() && typeof beforeOpenProject !== 'function') {
      showToast('This tab is read-only — another tab is editing this project.', 'info');
      return false;
    }
    if (store.isDirty()) {
      const choice = await askOpenDirtyChoice(project.name || 'Untitled', {
        canSaveCurrent: store.isWritable()
      });
      if (choice === 'cancel') return false;
      if (choice === 'save-first') {
        try {
          await cloudSave();
        } catch (err) {
          showToast(
            err instanceof TemplateApiError
              ? `Could not save current work: ${err.message}`
              : 'Could not save current work — open cancelled',
            'error'
          );
          return false;
        }
      }
    }
    // The dirty-choice/save flow above is asynchronous; re-check immediately
    // before the whole-state replacement in case a peer joined meanwhile.
    if (typeof canOpenProject === 'function' && !canOpenProject(project)) return false;
    if (typeof beforeOpenProject === 'function' && !(await beforeOpenProject(project))) {
      showToast('That project is already open in another tab.', 'info');
      return false;
    }
    // Announced, not painted over: the old renderLoading() replaced the whole
    // body with one line of centred text, so opening a project collapsed the
    // modal to a couple of hundred pixels on its way out.
    api?.setStatus(`Opening ${project.name || 'Untitled'}…`);
    try {
      // restoreCloudVersion loads the latest bundle via loadFromZip →
      // applyLoadedProjectState (slot synced) and sets cloudVersion = latest.
      await restoreCloudVersion(id, latestVersion, { requireWritable: true });
      eventBus.emit('project:restored', { projectId: id, version: latestVersion });
      showToast(`Opened ${project.name || 'Untitled'}`, 'success');
      api?.close();
      return true;
    } catch (err) {
      if (isLoadSupersededError(err)) {
        showToast(err.message, 'info');
        await api?.refresh();
        return false;
      }
      showToast(err instanceof TemplateApiError ? err.message : 'Could not open project', 'error');
      await api?.refresh();
      return false;
    }
  }

  const handle = openCollectionBrowser({
    title: 'My Projects',
    // PP-2: search has focus at time zero, through the kit.
    initialFocus: 'search',
    // PP-2: the ONE create-shaped thing this list can offer, as a tile in the
    // grid — named for what `toolbar:cloud-save` actually does. Hidden when
    // the open project is already a cloud project (it would just save a
    // version, which Save and history already says plainly).
    tiles: store.getState()?.cloudVersion
      ? {}
      : {
          trailing: [
            {
              id: 'save-to-cloud',
              kind: 'create',
              label: 'Save current project to cloud',
              description: 'Creates a cloud project from this draft',
              testid: 'projects-save-to-cloud-tile',
              onActivate: () => {
                handle?.close();
                eventBus.emit('toolbar:cloud-save');
              }
            }
          ]
        },
    testid: 'project-switcher',
    size: 'wide',
    overlayClassName: 'pb-projects-overlay',
    className: 'pb-history-modal',
    layout: 'grid',
    listTag: 'ul',
    itemTag: 'li',
    listAriaLabel: 'Projects',
    search: { placeholder: 'Search projects', ariaLabel: 'Search projects' },
    chips: false,
    // The remembered count, clamped 4..24: a fixed 6 made a twelve-project
    // modal jump its full height on every open.
    skeleton: { variant: 'project', count: () => skeletonCount() },
    illustration: 'projects',
    emptyCopy: {
      title: 'No saved projects yet.',
      body: 'Save this project to your account to see it here.'
    },
    filteredEmptyCopy: ({ query }) => ({ title: `No projects match “${query}”.` }),
    errorCopy: () => ({ title: loadErrorMessage || 'Failed to load projects' }),

    idOf: (project, i) => String(project?.id ?? i),

    load: async () => {
      loadErrorMessage = null;
      try {
        // `listProjects()` takes no signal (auth-api's list route is a plain
        // GET); a superseded run is discarded by the module's run-id guard.
        const resp = await listProjects();
        projects = Array.isArray(resp?.projects) ? resp.projects : [];
        rememberProjectCount(projects.length);
        // SB-5. ONLY here — after a load that actually succeeded — and only up
        // to the greatest `memberSince` in THIS result. Marking on modal-open,
        // or with a wall clock, would cover a share created between the load and
        // the write: it would be marked seen without ever having been shown, and
        // the recipient would never learn about that project.
        adoptProjectList(projects);
        void markSharesSeen(projects);
        return projects;
      } catch (err) {
        loadErrorMessage = err instanceof TemplateApiError ? err.message : null;
        throw err;
      }
    },

    adapt: (project) => {
      const id = String(project.id || '');
      const name = project.name || 'Untitled';
      // Collab Phase 2: shared (member-role) projects can't be deleted by this
      // account — bulk selection excludes them; Delete becomes Leave.
      const isMember = project.role === 'member';
      // SB-2 (A5): the project this tab has OPEN is not deletable from here.
      // The card already says "Editing now"; deleting it left the tab editing a
      // project that no longer exists, which is the most likely way to reach the
      // unavailable state by accident. Refusing is chosen over "delete it and
      // reset the tab" so the destructive action stays in one place and the
      // user's in-flight work is never touched by a click in another surface.
      const isOpen = id === currentProjectId;
      return {
        id,
        title: name,
        titleAttr: name,
        meta: `Edited ${relativeDate(project.updatedAt)}`,
        metaAttr: formatDate(project.updatedAt),
        // Donor parity: the search matches the project NAME, not the caption.
        searchText: name.toLowerCase(),
        className: 'pb-project-card',
        canSelect: !isMember && !isOpen,
        // THE POSTER IS THE OPEN BUTTON. It used to be a centred "Open" pill
        // that only existed inside a hover scrim, which meant the obvious
        // target — the artwork — did nothing. The current project gets the same
        // button rather than a dead label: openProject() short-circuits to
        // close() when the id already matches.
        poster: { kind: 'custom', build: () => buildPoster(project) },
        primaryAction: { label: 'Open', ariaLabel: `Open ${name}` },
        actions: [
          {
            id: 'history',
            // HR-7: the label says BOTH halves of what the button does. It
            // opens the project and then shows its timeline; a label reading
            // only "Version history" beside a card that is not open would hide
            // a whole-project load behind a small icon.
            label: isOpen ? `History for ${name}` : `Open ${name} and show its history`,
            tooltip: isOpen ? 'History' : 'Open and show history',
            icon: CARD_ICONS.history,
            slot: 'overlay'
          },
          isMember
            ? {
                id: 'leave',
                label: `Leave ${name}`,
                tooltip: 'Leave project',
                icon: CARD_ICONS.leave,
                danger: true,
                slot: 'overlay'
              }
            : {
                id: 'delete',
                label: `Delete ${name}`,
                // The tooltip is the whole explanation — a disabled control
                // with no stated reason reads as a bug.
                tooltip: isOpen ? 'Close this project before deleting it' : 'Delete project',
                icon: CARD_ICONS.trash,
                danger: true,
                disabled: isOpen,
                slot: 'overlay'
              }
        ],
        state: { current: project.id === currentProjectId },
        data: project
      };
    },

    /**
     * Thumbnails sit behind an access-checked route, so each one is fetched as
     * a blob and shown from an object URL. The module's pool bounds the
     * concurrency at 4; the URLs are process-global, so this modal releases
     * them on close — it is the only thumbnail consumer that can be open.
     */
    thumbnails: {
      resolve: (item) =>
        loadThumbnailObjectUrl(String(item.id), {
          thumbUpdatedAt: Number(item.data?.thumbUpdatedAt) || null
        }),
      releaseAll: () => releaseAllThumbnails()
    },

    selection: {
      noun: 'project',
      canSelect: (item) =>
        item.data?.role !== 'member' && String(item.data?.id || '') !== currentProjectId,
      bulkIntents: [
        {
          id: 'delete-selected',
          label: 'Delete',
          variant: 'danger',
          enabled: (items) => items.length > 0,
          // The module never deletes. It hands over the selected items and
          // awaits; the surface owns the confirm, the API calls, the optimistic
          // removal and the rollback.
          //
          // Still every selected project, not just the visible ones — see
          // hiddenSelectionCount() for why narrowing would be the worse bug.
          // The hidden count rides along so the confirm can name it.
          perform: (items) =>
            deleteProjects(
              items.map((item) => item.data),
              { hiddenCount: hiddenSelectionCount() }
            )
        }
      ],
      onChange: ({ count }) => {
        // `is-selecting` lands on the SCROLLER, never on the body: the body is
        // what the floating bar's strip is reserved in, and a class that
        // changes any box there would put back the reflow the out-of-flow bar
        // exists to remove. Here it only un-hides the resting checkboxes.
        api?.listEl.closest('.pb-coll-scroll')?.classList.toggle('is-selecting', count > 0);
      }
    },

    onActivate: (item) => openProject(item.data),

    onAction: (action, item) => {
      const project = item.data;
      if (action === 'history') {
        // HR-7. This used to open a version-list modal for ANY project without
        // opening the project — and its Restore then replaced the live state
        // anyway, at an OLD version, which is the more destructive half of the
        // same trip taken in the wrong order. The timeline previews the
        // selected version on the real canvas, and the preview seam requires
        // the detached state's projectId to match the LIVE one, so the project
        // has to be open first. That is not a workaround: it is the honest
        // ordering, and it is the one openProject() already guards with the
        // dirty choice, the tab lock and the read-only check.
        //
        // The rail is asked for through the SHELL'S event, never by importing
        // ui/history-rail/ here. That keeps the initiative revertable as one
        // directory plus shell.js — the property HR-0's gate exists to hold.
        // BC-7: History is ONE surface; a project's history opens it on the
        // cloud scope. The docked canvas view stays one click away from there.
        return openProject(project).then((opened) => {
          if (opened) eventBus.emit('toolbar:open-history-panel', { scope: 'cloud' });
        });
      }
      if (action === 'delete') return deleteProjects([project]);
      if (action === 'leave') return leaveProject(project);
    },

    onOpen(created) {
      api = created;
      created.body.setAttribute('data-role', 'body');
      // The donor look rides this class (padding, max-height); its sheet loads
      // after collection.css, so it wins the body box back. The bar's strip is
      // NOT reserved here — that is the module scroller's job, unconditionally.
      created.body.classList.add('pb-history-body');
      created.modal.querySelector('.pb-modal-header')?.classList.add('pb-history-header');
    },

    onRender(created) {
      for (const item of created.getItems()) {
        const el = created.itemEl(item.id);
        if (!el) continue;
        // The surface's own identity mirror (module identity stays data-cb-id).
        el.dataset.projectId = item.id;
        // The scrim variant of the kit checkbox: a plain box over a screenshot
        // has no contrast guarantee against whatever pixel sits behind it.
        el.querySelector('.pb-coll-check-input')?.classList.add('pb-checkbox-on-media');
      }
    }
  });
  if (!handle) return;
  api = handle;
}
